import { Badge, Group, Pagination, Table, Text, Tooltip } from '@mantine/core';

import { marginShare, type ProjectEconomics, type ProjectEconomicsPage } from '@/types/economics';
import { formatAmount } from './palette';

export interface ProjectEconomicsTableProps {
  page: ProjectEconomicsPage;
  teamNames: Record<string, string>;
  onPage: (page: number) => void;
  onOpen: (row: ProjectEconomics) => void;
}

function percentText(share: number | null): string {
  return share === null
    ? '—'
    : `${(share * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;
}

/** One row per piece of consumed work, newest first, with revenue against
 * cost in the display currency. A row whose figures leave lines out says so. */
export function ProjectEconomicsTable({
  page,
  teamNames,
  onPage,
  onOpen,
}: Readonly<ProjectEconomicsTableProps>) {
  if (page.items.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        No work in this period.
      </Text>
    );
  }
  const pages = Math.max(1, Math.ceil(page.total / page.pageSize));

  return (
    <>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Completed</Table.Th>
            <Table.Th>Work</Table.Th>
            <Table.Th>Client</Table.Th>
            <Table.Th ta="right">Revenue</Table.Th>
            <Table.Th ta="right">Cost</Table.Th>
            <Table.Th ta="right">Margin</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {page.items.map((row) => {
            const { totals } = row;
            const money = (value: number) => formatAmount(value, totals.currency);
            return (
              <Table.Tr
                key={`${row.source}/${row.sourceRef}`}
                style={{ cursor: 'pointer' }}
                onClick={() => onOpen(row)}
                data-testid={`economics-row-${row.sourceRef}`}
              >
                <Table.Td>{row.consumedAt.slice(0, 10)}</Table.Td>
                <Table.Td>
                  <Text size="sm">{row.label ?? row.projectCode ?? row.sourceRef}</Text>
                  <Text size="xs" c="dimmed">
                    {row.source === 'hub_report' ? 'Hub report' : 'Backoffice project'}
                    {row.backofficeProjectId ? ` · ${row.backofficeProjectId}` : ''}
                  </Text>
                </Table.Td>
                <Table.Td>{teamNames[row.teamId] ?? `Team ${row.teamId}`}</Table.Td>
                <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.revenue === null ? (
                    <Text size="sm" c="dimmed">
                      Unpriced
                    </Text>
                  ) : (
                    money(totals.revenue)
                  )}
                </Table.Td>
                <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {money(totals.cost)}
                </Table.Td>
                <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {money(totals.margin)}
                  <Text size="xs" c="dimmed">
                    {percentText(marginShare(totals))}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    {row.voided ? (
                      <Badge size="sm" color="gray" variant="light">
                        Voided
                      </Badge>
                    ) : null}
                    {totals.unrated > 0 ? (
                      <Tooltip label="Cost lines with no rate are not counted, so cost is a floor">
                        <Badge size="sm" color="orange" variant="light">
                          {totals.unrated} unrated
                        </Badge>
                      </Tooltip>
                    ) : null}
                    {totals.unconverted > 0 ? (
                      <Tooltip label="Lines with no exchange rate in the week before are left out">
                        <Badge size="sm" color="orange" variant="light">
                          {totals.unconverted} unconverted
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {pages > 1 ? (
        <Group justify="center" mt="md">
          <Pagination total={pages} value={page.page} onChange={onPage} />
        </Group>
      ) : null}
    </>
  );
}
